import ExpandIcon from '@/assets/images/Expand.svg';
import { AvatarFallback, AvatarImage, Avatar as ImageAvatar } from '@/components/ui/avatar';
import mediaContext from '@/context/media-context';
import zoomContext from '@/context/zoom-context';
import { usePrevious } from '@/hooks';
import { cn } from '@/lib/utils';
import { Role } from '@/Types/gql/graphql';
import { authState } from '@/vars/auth';
import { useReactiveVar } from '@apollo/client';
import {
  Participant,
  type VideoPlayer,
  type VideoPlayerContainer,
  VideoQuality,
} from '@zoom/videosdk';
import { capitalize } from 'lodash';
import {
  DetailedHTMLProps,
  DOMAttributes,
  HTMLAttributes,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useActiveVideo } from '../hooks/useActiveVideo';
import { useNetworkQuality } from '../hooks/useNetworkQuality';
import { useParticipantsChange } from '../hooks/useParticipantsChange';
import { useSpotlightVideo } from '../hooks/useSpotlightVideo';
import { useVideoAspect } from '../hooks/useVideoAspectRatio';
import { miniScreenUserIdVar, sessionVar } from '../meetingVar';
import Avatar from './avatar';
import VideoFooter from './video-footer';
import './video.css';

type CustomElement<T> = Partial<T & DOMAttributes<T> & { children: any }>;

/* eslint-disable @typescript-eslint/no-namespace */
declare global {
  namespace JSX {
    interface IntrinsicElements {
      ['video-player']: DetailedHTMLProps<HTMLAttributes<VideoPlayer>, VideoPlayer> & {
        class?: string;
      };
      ['video-player-container']: CustomElement<VideoPlayerContainer> & { class?: string };
    }
  }
}
/* eslint-enable @typescript-eslint/no-namespace */

const VideoContainer = () => {
  const zmClient = useContext(zoomContext);
  const { mediaStream } = useContext(mediaContext);
  const { userDetails } = useReactiveVar(authState);
  const sessionData = useReactiveVar(sessionVar);
  const miniScreenUserId = useReactiveVar(miniScreenUserIdVar);

  const shareViewRef = useRef<{ selfShareRef: HTMLCanvasElement | HTMLVideoElement | null }>(null);
  const videoPlayerListRef = useRef<Record<string, VideoPlayer>>({});

  const [subscribers, setSubscribers] = useState<number[]>([]);
  const [participants, setParticipants] = useState(zmClient.getAllUser());
  const [spotlightUsers, setSpotlightUsers] = useState<Participant[]>();

  const aspectRatio = useVideoAspect(zmClient);
  const activeVideo = useActiveVideo(zmClient);
  const networkQuality = useNetworkQuality(zmClient);
  const previousSubscribers = usePrevious(subscribers);

  useParticipantsChange(zmClient, (participants) => {
    let pageParticipants: Participant[] = [];
    if (participants.length > 0) {
      if (participants.length === 1) {
        pageParticipants = participants;
      } else {
        pageParticipants = participants
          .filter((user) => user.userId !== zmClient.getSessionInfo().userId)
          .sort((user1, user2) => Number(user2.bVideoOn) - Number(user1.bVideoOn));
        const currentUser = zmClient.getCurrentUserInfo();
        if (currentUser) {
          pageParticipants.splice(1, 0, currentUser);
        }
      }
    }
    setParticipants(pageParticipants);
    setSubscribers(pageParticipants.filter((user) => user.bVideoOn).map((u) => u.userId));
  });
  useSpotlightVideo(zmClient, mediaStream, (p) => {
    setSpotlightUsers(p);
  });
  const setVideoPlayerRef = (userId: number, element: VideoPlayer | null) => {
    if (element) {
      videoPlayerListRef.current[`${userId}`] = element;
    }
  };
  useEffect(() => {
    const addedUsers = subscribers.filter((user) => !(previousSubscribers || []).includes(user));
    const removedUsers = (previousSubscribers || []).filter((user) => !subscribers.includes(user));
    if (removedUsers.length > 0) {
      removedUsers.forEach((userId) => {
        mediaStream?.detachVideo(userId);
      });
    }
    if (addedUsers.length > 0) {
      addedUsers.forEach((userId) => {
        const attachment = videoPlayerListRef.current[`${userId}`];
        if (attachment) {
          mediaStream?.attachVideo(userId, VideoQuality.Video_720P, attachment);
        }
      });
    }
  }, [subscribers, previousSubscribers, mediaStream]);

  const sortedParticipants = useMemo(() => {
    if (spotlightUsers?.length) {
      const splightUserIds = spotlightUsers.map((u) => u.userId);
      return participants
        .filter((user) => !splightUserIds.includes(user.userId))
        .concat(
          participants
            .filter((user) => splightUserIds.includes(user.userId))
            .map((user) => ({ spotlighted: true, ...user })),
        );
    }
    return participants;
  }, [participants, spotlightUsers]);

  useEffect(() => {
    const videoContainer = document.querySelector('.video-container-wrap');
    if (videoContainer) {
      const shadowRoot = videoContainer.shadowRoot;
      if (shadowRoot) {
        const canvas = shadowRoot.querySelector('canvas');
        if (canvas) {
          canvas.style.left = 'auto';
          canvas.style.zIndex = '100';
        }
      }
    }
  }, []);

  return (
    <div className='h-[calc(100vh-73px)] flex flex-col bg-black'>
      <div className='flex-grow p-3 md:px-14 md:py-4'>
        <div className='flex items-center justify-center bg-[#16181B] h-full rounded-xl relative'>
          <video-player-container
            class='h-full w-full max-h-[calc(100vh-180px)] video-container-wrap'
            autofocus
          >
            <ul className='h-full user-list flex gap-4'>
              {sortedParticipants.map((user) => {
                const maxWidth = '100%';
                return (
                  <div
                    className={cn(
                      'video-cell',
                      user.userId === miniScreenUserId
                        ? 'sender-video border-[#4e4e4e] z-20 relative h-[129px] w-[230px]'
                        : 'receiver-video h-full max-h-[calc(100vh-173px)] relative',
                    )}
                    key={user.userId}
                    style={
                      aspectRatio[`${user.userId}`]
                        ? {
                            aspectRatio: aspectRatio[`${user.userId}`],
                            maxWidth,
                          }
                        : {
                            maxWidth,
                          }
                    }
                  >
                    {user.bVideoOn && (
                      <div
                        className={cn(
                          'h-full w-full',
                          user.userId === miniScreenUserId ? 'h-[129px] w-[230px] z-20' : '',
                        )}
                      >
                        <video-player
                          key={user.userId}
                          class={cn('video-player w-full h-full z-30')}
                          ref={(element) => {
                            setVideoPlayerRef(user.userId, element);
                          }}
                        />
                      </div>
                    )}
                    <Avatar
                      participant={user}
                      participants={sortedParticipants}
                      key={user.userId}
                      isActive={activeVideo === user.userId}
                      networkQuality={networkQuality[`${user.userId}`]}
                    />
                  </div>
                );
              })}
            </ul>
          </video-player-container>
          {sortedParticipants.length < 2 ? (
            <div
              className={cn(
                'absolute w-full max-w-[1000px] h-full flex flex-col justify-center items-center gap-5 backdrop-blur-[170.5px] bg-[#272727A1] bg-cover',
                miniScreenUserId !== zmClient?.getCurrentUserInfo()?.userId
                  ? 'w-[230px] h-[129px] right-1 top-2 rounded-xl'
                  : '',
              )}
            >
              <ImageAvatar
                className={cn(
                  'w-40 h-40  bg-[#515764] z-20',
                  miniScreenUserId !== zmClient?.getCurrentUserInfo()?.userId ? 'w-12 h-12' : '',
                )}
              >
                <AvatarImage
                  src={
                    userDetails?.role === Role.Trainee
                      ? sessionData?.trainerIdUser?.profileImage || ''
                      : sessionData?.traineeIdUser?.profileImage || ''
                  }
                  alt='trainer Profile picture'
                />
                <AvatarFallback
                  className={cn(
                    'text-3xl font-bold',
                    miniScreenUserId !== zmClient?.getCurrentUserInfo()?.userId ? 'text-lg' : '',
                  )}
                >
                  {userDetails?.role === Role.Trainee
                    ? sessionData?.trainerIdUser?.firstName?.charAt(0) || ''
                    : sessionData?.traineeIdUser?.displayName?.charAt(0) || ''}
                </AvatarFallback>
              </ImageAvatar>
              <div className='flex flex-col items-center gap-3 z-20'>
                <h3
                  className={cn(
                    'text-[20px] font-semibold',
                    miniScreenUserId !== zmClient?.getCurrentUserInfo()?.userId
                      ? 'text-sm font-normal'
                      : '',
                  )}
                >
                  {/* {capitalize(
                    userDetails?.role === Role.Trainee
                      ? sessionData?.trainerIdUser?.firstName || ''
                      : sessionData?.traineeIdUser?.displayName || '',
                  )} */}
                  {userDetails?.role === Role.Trainee
                    ? capitalize(sessionData?.trainerIdUser?.firstName || '') || ''
                    : capitalize(sessionData?.traineeIdUser?.firstName || '') +
                        ' ' +
                        capitalize(sessionData?.traineeIdUser?.lastName || '') || ''}
                </h3>
                <h4
                  className={cn(
                    'font-normal',
                    miniScreenUserId !== zmClient?.getCurrentUserInfo()?.userId ? 'text-xs' : '',
                  )}
                >
                  {userDetails?.role === Role.Trainee
                    ? 'Waiting for trainer to join'
                    : 'Waiting for customer to join'}
                </h4>
              </div>
              {miniScreenUserId !== zmClient?.getCurrentUserInfo()?.userId ? (
                <div
                  className='absolute top-4 right-3 flex items-center space-x-2 text-sm rounded-xl cursor-pointer z-20'
                  onClick={() => miniScreenUserIdVar(zmClient?.getCurrentUserInfo()?.userId)}
                >
                  <img src={ExpandIcon} alt='expand-icon' />
                </div>
              ) : null}
              <div className='absolute w-full max-w-[1000px] h-full backdrop-blur-[170.5px] bg-[#272727A1] z-10'></div>
            </div>
          ) : (
            ''
          )}
        </div>
      </div>
      <VideoFooter
        className='video-operations'
        sharing
        selfShareCanvas={shareViewRef.current?.selfShareRef}
      />
    </div>
  );
};
export default VideoContainer;
